// Org-wide customer roll-up (read-only). Inviting & scoping customers now
// happens per-portal in each portal's Customers tab. This page is a directory
// of every external customer across the workspace, with resend/remove actions.
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { ArrowLeft, Trash, ArrowsClockwise, Lifebuoy, Copy, Warning, Envelope, Gear, CaretRight } from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import ServiceDeskUpsell from '../../components/serviceDesk/ServiceDeskUpsell';

export default function CustomersPage() {
  const { API, getOrgHeaders, currentOrg } = useAuth();
  const { caps } = useSubscription();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [portalMap, setPortalMap] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastInviteLink, setLastInviteLink] = useState(null);
  const [lastEmailStatus, setLastEmailStatus] = useState(null);

  const sdEnabled = caps?.service_desk_enabled === true;
  const isAdmin = ['owner', 'admin'].includes(currentOrg?.role);

  const load = useCallback(async () => {
    if (!currentOrg || !sdEnabled) return;
    try {
      const [cRes, pRes] = await Promise.all([
        axios.get(`${API}/service-desk/customers`, { withCredentials: true, headers: getOrgHeaders() }),
        axios.get(`${API}/service-desk/portals`, { withCredentials: true, headers: getOrgHeaders() }).catch(() => ({ data: [] })),
      ]);
      setCustomers(cRes.data || []);
      setPortalMap(Object.fromEntries((pRes.data || []).map(p => [p.portal_id, p.name])));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [currentOrg, sdEnabled, API, getOrgHeaders]);

  useEffect(() => { load(); }, [load]);

  if (!sdEnabled) return <ServiceDeskUpsell />;
  if (!isAdmin) return <div className="flex-1 flex items-center justify-center bg-zinc-50"><p className="text-sm text-zinc-500">Admins only.</p></div>;

  const resend = async (id) => {
    try {
      const { data } = await axios.post(`${API}/service-desk/customers/${id}/resend-invite`, {},
        { withCredentials: true, headers: getOrgHeaders() });
      setLastInviteLink(data.invite_link);
      setLastEmailStatus({ sent: data.email_sent, error: data.email_error });
      toast.success('Magic link re-generated');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Remove this customer from the whole workspace?')) return;
    try {
      await axios.delete(`${API}/service-desk/customers/${id}`, { withCredentials: true, headers: getOrgHeaders() });
      setCustomers(prev => prev.filter(c => c.customer_id !== id));
      toast.success('Removed');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="flex-1 overflow-auto bg-zinc-50" data-testid="sd-customers-page">
      <div className="max-w-5xl mx-auto px-8 py-6">
        <button onClick={() => navigate('/service-desk')} className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 mb-4">
          <ArrowLeft size={14} /> Back to queue
        </button>
        <div className="mb-6">
          <div className="flex items-center gap-2 text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">
            <Lifebuoy size={14} weight="fill" /> Service Desk
          </div>
          <h1 className="text-2xl font-bold text-zinc-900 font-['Outfit']">User directory</h1>
          <p className="text-sm text-zinc-500 mt-0.5">Every external customer across your workspace.</p>
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 ring-1 ring-emerald-200 px-2.5 py-1 text-[11px] font-medium text-emerald-800" data-testid="sd-customers-free-badge">
            <Lifebuoy size={12} weight="fill" /> Free &amp; unlimited — customers are never charged as seats and only ever see your public portal (not your internal queues).
          </div>
        </div>

        <div className="mb-4 rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 flex items-start gap-2" data-testid="sd-customers-rollup-note">
          <Gear size={14} weight="fill" className="mt-0.5 flex-shrink-0" />
          <span>This is an org-wide directory. To <strong>invite a customer</strong> and control who can access a portal, open <strong>Service Desk → Settings → (a portal) → Customers</strong>.</span>
        </div>

        {lastInviteLink && (
          <div className={`mb-4 rounded-md p-4 text-xs flex items-start gap-2 ring-1 ${
            lastEmailStatus?.sent ? 'bg-emerald-50 ring-emerald-200 text-emerald-900' : 'bg-amber-50 ring-amber-300 text-amber-900'
          }`} data-testid="sd-invite-link-banner">
            {lastEmailStatus?.sent ? (
              <Envelope size={14} weight="fill" className="text-emerald-600 mt-0.5 flex-shrink-0" />
            ) : (
              <Warning size={14} weight="fill" className="text-amber-600 mt-0.5 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold">
                {lastEmailStatus?.sent ? 'Magic link emailed to customer.' : `Email delivery FAILED — ${lastEmailStatus?.error || 'unknown error'}. Copy this link and send it manually:`}
              </p>
              <p className="font-mono truncate text-[11px] mt-1 select-all">{lastInviteLink}</p>
            </div>
            <button onClick={() => { navigator.clipboard.writeText(lastInviteLink); toast.success('Copied'); }}
              className="px-2 py-1 rounded hover:bg-white/40 flex-shrink-0">
              <Copy size={13} />
            </button>
          </div>
        )}

        {loading ? <p className="text-center py-8 text-zinc-400">Loading…</p> : customers.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-12 text-center">
            <p className="text-sm text-zinc-500">No customers yet. Invite them from a portal Customers tab.</p>
          </div>
        ) : (
          <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[11px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <tr>
                  <th className="text-left py-2 px-4 font-semibold">Name</th>
                  <th className="text-left py-2 px-4 font-semibold">Email</th>
                  <th className="text-left py-2 px-4 font-semibold">Status</th>
                  <th className="text-left py-2 px-4 font-semibold">Portals</th>
                  <th className="text-right py-2 px-4 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {customers.map(c => {
                  const pids = c.portal_ids || [];
                  const isOpen = expanded === c.customer_id;
                  return (
                  <React.Fragment key={c.customer_id}>
                  <tr className="border-b border-zinc-100" data-testid={`sd-customer-row-${c.customer_id}`}>
                    <td className="py-2.5 px-4 font-medium text-zinc-900">{c.name}</td>
                    <td className="py-2.5 px-4 text-zinc-600">{c.email}</td>
                    <td className="py-2.5 px-4">
                      {(c.password_set_at || c.self_signup) ? (
                        <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[11px] font-semibold ring-1 ring-emerald-200">Active</span>
                      ) : c.external_ref?.source === 'jira' ? (
                        <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[11px] font-semibold ring-1 ring-blue-200" title="Imported from Jira — no invitation email was sent">Imported</span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[11px] font-semibold ring-1 ring-amber-200">Invite pending</span>
                      )}
                    </td>
                    <td className="py-2.5 px-4">
                      {pids.length === 0 ? (
                        <span className="text-xs text-zinc-400">None</span>
                      ) : (
                        <button onClick={() => setExpanded(isOpen ? null : c.customer_id)}
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                          data-testid={`sd-customer-portals-toggle-${c.customer_id}`}>
                          {pids.length} portal{pids.length === 1 ? '' : 's'}
                          <CaretRight size={11} className={`transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                        </button>
                      )}
                    </td>
                    <td className="py-2.5 px-4 text-right flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => resend(c.customer_id)} title="Re-generate magic link"
                        data-testid={`sd-customer-resend-${c.customer_id}`}>
                        <ArrowsClockwise size={13} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => del(c.customer_id)} className="text-red-600"
                        data-testid={`sd-customer-delete-${c.customer_id}`}>
                        <Trash size={13} />
                      </Button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-zinc-50/70" data-testid={`sd-customer-portals-${c.customer_id}`}>
                      <td colSpan={5} className="px-4 py-2.5">
                        <div className="flex flex-wrap gap-1.5">
                          {pids.map(pid => (
                            <span key={pid} className="text-[11px] px-2 py-0.5 rounded bg-white text-zinc-700 ring-1 ring-zinc-200">
                              {portalMap[pid] || pid}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
